import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { parseElectionDate } from '../../../../lib/date-utils';
import { useGuide } from '../../hooks/useGuide';

const CATEGORY_ORDER = ['federal', 'state', 'local', 'judges', 'measures'];
const CATEGORY_LABELS = {
  federal: 'Federal',
  state: 'State',
  local: 'County & Local',
  judges: 'Judges',
  measures: 'Ballot Measures',
};

export default function GuideSaveAndShare() {
  const navigate = useNavigate();
  const { lookupResult, selections, selectedCount, totalCount } = useGuide();
  const sheetRef = useRef(null);

  useEffect(() => {
    if (!lookupResult) navigate('/', { replace: true });
  }, [lookupResult, navigate]);

  if (!lookupResult) return null;

  const { ballot, election } = lookupResult;
  const pct = totalCount > 0 ? Math.round((selectedCount / totalCount) * 100) : 0;

  function getSelectionLabel(contest) {
    const sel = selections[contest.id];
    if (!sel) return null;
    if (contest.measure) return sel === 'for' ? 'For' : 'Against';
    const candidate = contest.candidates.find((c) => c.id === sel);
    return candidate ? candidate.name : null;
  }

  const pickedContests = CATEGORY_ORDER.flatMap((cat) =>
    (ballot.contests?.[cat] ?? []).filter((c) => selections[c.id])
  );

  return (
    <div>
      <p className="g-label">Step 4 — Your Cheat Sheet</p>
      <h1 className="g-h1">{selectedCount === totalCount ? "You're done." : `${pct}% done.`}</h1>
      <p className="g-body" style={{ marginBottom: 24 }}>
        {selectedCount === totalCount
          ? "Here's every pick you made. Screenshot it or print it before you head to the polls."
          : `You've decided ${selectedCount} of ${totalCount} races. You can still print what you have.`}
      </p>

      {selectedCount < totalCount && (
        <button
          className="g-btn-ghost"
          onClick={() => navigate('/guide/ballot-checklist')}
          style={{ marginBottom: 16 }}
        >
          ← Finish my picks ({totalCount - selectedCount} left)
        </button>
      )}

      {/* Cheat sheet */}
      <div
        ref={sheetRef}
        style={{
          background: '#111',
          border: '1px solid #333',
          borderRadius: 'var(--g-radius)',
          padding: '20px',
          marginBottom: 16,
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            color: 'var(--g-orange)',
            marginBottom: 4,
          }}
        >
          My Voting Guide
        </div>
        <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 4 }}>{election.name}</div>
        <div style={{ fontSize: 13, color: 'var(--g-muted)', marginBottom: 16 }}>
          {parseElectionDate(election.electionDate).toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })}
        </div>

        {pickedContests.length === 0 ? (
          <div style={{ fontSize: 14, color: 'var(--g-muted)' }}>No picks yet.</div>
        ) : (
          CATEGORY_ORDER.map((cat) => {
            const contests = (ballot.contests?.[cat] ?? []).filter((c) => selections[c.id]);
            if (contests.length === 0) return null;
            return (
              <div key={cat} style={{ marginBottom: 12 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: 'var(--g-muted)',
                    marginBottom: 6,
                  }}
                >
                  {CATEGORY_LABELS[cat]}
                </div>
                {contests.map((c) => (
                  <div
                    key={c.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      paddingBottom: 6,
                      marginBottom: 6,
                      borderBottom: '1px solid #1a1a1a',
                    }}
                  >
                    <span style={{ fontSize: 14, color: '#ccc', flex: 1 }}>
                      {c.officeName ?? c.measure?.title}
                    </span>
                    <span
                      style={{
                        fontSize: 15,
                        fontWeight: 800,
                        color: 'var(--g-white)',
                        marginLeft: 12,
                        flexShrink: 0,
                      }}
                    >
                      {getSelectionLabel(c)}
                    </span>
                  </div>
                ))}
              </div>
            );
          })
        )}

        <div
          style={{
            marginTop: 16,
            paddingTop: 12,
            borderTop: '1px solid #222',
            fontSize: 11,
            color: '#555',
          }}
        >
          Note: Photography of your marked ballot may be restricted in the polling booth. Know your
          state's rules.
        </div>
      </div>

      <button className="g-btn-ghost" onClick={() => window.print()} style={{ marginBottom: 8 }}>
        Print cheat sheet
      </button>

      <button className="g-btn-ghost" onClick={() => navigate('/guide/ballot-checklist')}>
        ← Edit my picks
      </button>
    </div>
  );
}
